/* eslint-disable max-len */
import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { MESSAGES } from '@app/constant';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateMarkMutation, useLazyGetMarkByIdQuery, useLazyGetMarksQuery, useUpdateMarkMutation } from '@modules/mark';
import { IMark } from '@modules/mark/types';
import { IMarket } from '@modules/market/types';
import { useLazyGetMarketsQuery } from '@modules/market';

import { IFlowProcess, IMarkFormData } from './helper/types';
import { createMarkSchema, initialFlowState, initialFormState } from './helper/data';
import { prepareClonedData } from './helper/utils';

export const useMarkLogic = () => {
  const { id } = useParams()

  const [gotMarketList, setMarketList] = useState<IMarket[]>([])
  const [productKindErrorMessage, setProductKindErrorMessage] = useState('')
  const [createMark] = useCreateMarkMutation()
  const [updateMark] = useUpdateMarkMutation()
  const [getMarkById] = useLazyGetMarkByIdQuery();
  const [flowProcess, setFlowProcess] = useState<IFlowProcess>(initialFlowState)

  const methods = useForm<IMarkFormData>({
    // @ts-ignore
    resolver: yupResolver(createMarkSchema),
    mode: 'onSubmit',
    defaultValues: initialFormState
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = methods;

  const iceCream = watch('iceCream')
  const pastry = watch('pastry')
  const pudding = watch('pudding')
  const selectedMarket = watch('marketId')

  const [getMarkets] = useLazyGetMarketsQuery()

  const handleLoadingState = (isLoading: boolean, successMessage: string = '') => {
    setFlowProcess((prev) => ({
      ...prev,
      isButtonLoading: isLoading,
      isSuccess: successMessage,
      largeImageMessage: '',
    }));
  };

  const onSubmit = async (data: IMarkFormData) => {
    handleLoadingState(true);

    try {
      if (!data.iceCream && !data.pastry && !data.pudding) {
        setProductKindErrorMessage('Select one of these checkboxes')
        handleLoadingState(false)

        return
      }

      setProductKindErrorMessage('')

      const clonedData = prepareClonedData(data);

      if (id) {
        await updateMark({ id, body: clonedData }).unwrap()
        getMarkData()
      } else {
        await createMark(clonedData).unwrap();
        reset(initialFormState);
      }

      handleLoadingState(false, id ? MESSAGES.mapMarkHasBeenSuccessfullyUpdated : MESSAGES.mapMarkHasBeenSuccessfullyCreated);
    } catch (error: any) {
      handleLoadingState(false, error?.data?.message || MESSAGES.somethingWentWrong);
    }
  };

  const getMarkData = async () => {
    if (!id) {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))

      return
    }

    try {
      const mark = await getMarkById(id).unwrap()

      reset({ ...mark });
    } catch (error) {
      console.log("Error", error);
    } finally {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))
    }
  }

  const getMarksList = async () => {
    const marksResponse = await getMarkets({ offset: 0, limit: 100 }).unwrap();

    setMarketList(marksResponse?.markets || [])
  }

  useEffect(() => {
    getMarksList()
  }, [id])

  useEffect(() => {
    getMarkData()
  }, [id])

  return {
    ...flowProcess,
    gotMarketList,
    selectedMarket,
    paramId: id,
    iceCream,
    pastry,
    pudding,
    methods,
    errors,
    productKindErrorMessage,
    watch,
    setValue,
    setError,
    setFlowProcess,
    onSubmit,
    register,
    handleSubmit,
  }
}
